import React from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from 'emotion-theming';
import NoSSR from 'react-no-ssr';
import { connect } from 'react-redux';

import HandOffLink from '../../../utils/urls/HandOffLink';
import CurrencyDropdown from '../CurrencyDropdown';
import IconLinks from './IconLinks';
import Logo from '../../../assets/svg/Logo';
import PrivacyNotice from '../../../assets/pdfs/PP_CCPA_Privacy_Notice_010120.pdf';
import typography from '../../../styling/mixins/typography';
import colors from '../../../styling/colors';
import { breakpoints } from '../../../styling/themes/FlexboxGridThemes';

import { Grid, Col, Row } from 'react-styled-flexboxgrid';

import withTrackingPropsProvider from '../../../hocs/withTrackingPropsProvider';
import {
  DESKTOP_START_QUERY,
  MOBILE_END_QUERY,
  FOOTER_MAX_WIDTH,
} from '../../../styling/mixins/responsive';

const FooterWrapper = styled.footer`
  margin: auto;
  max-width: ${FOOTER_MAX_WIDTH}px;
  padding: 0;
  border-top: 1px solid ${colors('gallery')};
`;

const FooterGrid = styled(Grid)`
  margin: 1.5rem 0 2.5rem;
  padding: 0;

  @media (${DESKTOP_START_QUERY}) {
    padding: 0 1rem;
    margin-top: 3rem;
  }
`;

const LinksRow = styled(Row)`
  margin: 0;

  @media (${DESKTOP_START_QUERY}) {
    margin: 3rem 0 4rem;
  }
`;

const FullWidthColumn = styled(Col)`
  @media (${MOBILE_END_QUERY}) {
    padding: 0;
  }
`;

const LogoWrapper = styled.div`
  margin-bottom: 1rem;
  width: 7rem;

  @media (${DESKTOP_START_QUERY}) {
    width: 8rem;
  }
`;

const Links = styled.div`
  color: ${colors('lapel')};
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  align-items: center;
  height: 100%;

  @media (${DESKTOP_START_QUERY}) {
    align-items: flex-start;
  }
`;

const Header = styled.div`
  ${typography.trackedStyles} margin-top: .5rem;
  margin-bottom: 1rem;
`;

const StayConnectedHeader = styled(Header)`
  margin-bottom: 1.5rem;
  display: none;

  @media (${DESKTOP_START_QUERY}) {
    display: block;
  }
`;

const StyledHandOffLink = styled(HandOffLink)`
  line-height: 2rem;

  &:hover {
    color: ${colors('boulder')};
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (${DESKTOP_START_QUERY}) {
    align-items: flex-start;
  }
`;

const TermsAndCurrencyRow = styled(Row)`
  color: ${colors('boulder')};
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  justify-content: center;
  padding-left: 8px;
  margin: 0;

  @media (min-width: 361px) {
    flex-direction: row;
    padding-left: 0px;
  }

  @media (${MOBILE_END_QUERY}) {
    padding-bottom: 0.25rem;
  }
`;

const SeparationPipe = styled.div`
  color: ${colors('white')};
  display: block;

  @media (min-width: 361px) {
    color: ${colors('alto')};
    display: inline;
    padding: 0 0.75rem;
  }

  @media (${DESKTOP_START_QUERY}) {
    color: ${colors('alto')};
    display: inline;
    padding: 0 1rem;
  }
`;

//The padding makes the two terms item equal size to center the pipe
const TermsLink = styled(HandOffLink)`
  color: ${colors('boulder')};
  display: inline-block;
`;

const TrackedFooterWrapper = withTrackingPropsProvider(FooterWrapper);

const TRACKING_PROPS = {
  block_name: 'footer',
  block_row: 'footer',
};

const flexBoxGridTheme = {
  flexboxgrid: {
    gridSize: 12,
    gutterWidth: 1,
    outerMargin: 1,
    mediaQuery: 'only screen',
    container: {
      sm: 46,
      md: 61,
      lg: 76,
    },
    breakpoints,
  },
};

function PolicyLinks() {
  return (
    <>
      <TermsLink
        identifier="paperless-post-privacy-notice"
        href={PrivacyNotice}
      >
        Privacy Notice for CA Residents
      </TermsLink>
      <SeparationPipe>|</SeparationPipe>
      <TermsLink
        identifier="paperless-post-cookie-policy"
        href="/cookie-policy"
      >
        Opt Out Of Targeted Ads
      </TermsLink>
    </>
  );
}

function Footer({ isMobile }) {
  return (
    <TrackedFooterWrapper tracking={TRACKING_PROPS}>
      <ThemeProvider theme={flexBoxGridTheme}>
        <FooterGrid fluid={true}>
          <LinksRow>
            <Col xs={12} sm={3}>
              <LogoContainer>
                <LogoWrapper>
                  <Logo />
                </LogoWrapper>
              </LogoContainer>
            </Col>
            <Col xs={6} sm={3}>
              <Links>
                <Header>Help</Header>
                <StyledHandOffLink
                  identifier="paperless-post-help"
                  href="https://paperlesspost.zendesk.com/hc/en-us"
                >
                  FAQ
                </StyledHandOffLink>
                <StyledHandOffLink
                  identifier="paperless-post-contact"
                  href="/contact"
                >
                  Contact us
                </StyledHandOffLink>
                <StyledHandOffLink
                  identifier="paperless-post-sitemap"
                  href="/sitemap"
                >
                  Sitemap
                </StyledHandOffLink>
                <StyledHandOffLink
                  identifier="paperless-post-unsubscribe"
                  href="/unsubscribe"
                >
                  Unsubscribe
                </StyledHandOffLink>
              </Links>
            </Col>
            <Col xs={6} sm={3}>
              <Links>
                <Header>Company</Header>
                <StyledHandOffLink
                  identifier="paperless-post-about"
                  href="/about/about"
                >
                  About
                </StyledHandOffLink>
                <StyledHandOffLink
                  identifier="paperless-post-blog"
                  href="https://www.paperlesspost.com/blog/"
                >
                  Blog
                </StyledHandOffLink>
                <StyledHandOffLink
                  identifier="paperless-post-jobs"
                  href="/about/jobs"
                >
                  Jobs
                </StyledHandOffLink>
              </Links>
            </Col>
            <FullWidthColumn xs={12} sm={3}>
              <Links>
                <StayConnectedHeader>Stay Connected</StayConnectedHeader>
                <IconLinks />
              </Links>
            </FullWidthColumn>
          </LinksRow>
          <TermsAndCurrencyRow>
            © {new Date().getFullYear()} Paperless Post ®
            <SeparationPipe>|</SeparationPipe>
            <TermsLink
              identifier="paperless-post-terms-of-service"
              href="/terms-of-service"
            >
              Terms & Privacy
            </TermsLink>
            <SeparationPipe>|</SeparationPipe>
            {!isMobile ? (
              <>
                <PolicyLinks />
                <SeparationPipe>|</SeparationPipe>
              </>
            ) : null}
            <NoSSR>
              <CurrencyDropdown />
            </NoSSR>
          </TermsAndCurrencyRow>
          {isMobile ? (
            <TermsAndCurrencyRow>
              <PolicyLinks />
            </TermsAndCurrencyRow>
          ) : null}
        </FooterGrid>
      </ThemeProvider>
    </TrackedFooterWrapper>
  );
}

const mapStateToProps = ({ env: { isMobile } }) => ({ isMobile });

export default connect(mapStateToProps)(Footer);
